/**
 * PDF Flipbook
 * --------------------------------------------------------------------------
 * Renders a PDF as a realistic page-flipping book.
 * Uses PDF.js (page rendering) + StPageFlip (flip animation), loaded on demand
 * from CDN the first time a flipbook appears on the page.
 */
(function () {
  'use strict';

  var PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  var PAGEFLIP_SRC = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js';

  var loaders = {};

  function loadScript(src) {
    if (loaders[src]) return loaders[src];
    loaders[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(s);
    });
    return loaders[src];
  }

  function loadDependencies() {
    return loadScript(PDFJS_SRC)
      .then(function () {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        }
        return loadScript(PAGEFLIP_SRC);
      });
  }

  class PdfFlipbook extends HTMLElement {
    constructor() {
      super();
      this.pageFlip = null;
      this.rendered = false;
    }

    connectedCallback() {
      if (this.rendered) return;
      this.rendered = true;

      this.url = this.dataset.pdfUrl;
      this.doublePage = this.dataset.pageMode !== 'single';
      this.maxRenderWidth = parseInt(this.dataset.quality, 10) || 1200;
      this.maxHeight = parseInt(this.dataset.height, 10) || 720;
      var shadow = parseInt(this.dataset.shadow, 10);
      this.shadowOpacity = (isNaN(shadow) ? 30 : shadow) / 100;

      this.viewport = this.querySelector('[data-flipbook-viewport]');
      this.stage = this.querySelector('.pdf-flipbook__stage');
      this.statusEl = this.querySelector('[data-flipbook-status]');
      this.pageLabel = this.querySelector('[data-flipbook-page]');
      this.prevBtn = this.querySelector('[data-flipbook-prev]');
      this.nextBtn = this.querySelector('[data-flipbook-next]');
      this.fsBtn = this.querySelector('[data-flipbook-fullscreen]');

      if (!this.url) {
        this.setStatus('No PDF selected. Add a PDF file URL in the theme editor.');
        return;
      }

      this.bindControls();

      var self = this;
      loadDependencies()
        .then(function () {
          return self.buildFlipbook();
        })
        .catch(function (err) {
          console.error('[pdf-flipbook]', err);
          self.setStatus('Could not load the flipbook. Please try again later.');
        });
    }

    disconnectedCallback() {
      if (this.pageFlip) {
        try {
          this.pageFlip.destroy();
        } catch (e) {
          /* noop */
        }
        this.pageFlip = null;
      }
    }

    setStatus(message) {
      if (!this.statusEl) return;
      if (message) {
        this.statusEl.textContent = message;
        this.statusEl.hidden = false;
      } else {
        this.statusEl.hidden = true;
      }
    }

    bindControls() {
      var self = this;
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          if (self.pageFlip) self.pageFlip.flipPrev();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          if (self.pageFlip) self.pageFlip.flipNext();
        });
      }
      if (this.fsBtn) {
        this.fsBtn.addEventListener('click', function () {
          self.toggleFullscreen();
        });
        document.addEventListener('fullscreenchange', function () {
          var active = document.fullscreenElement === self;
          self.fsBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
          // Give the flip engine a tick to pick up the new size.
          setTimeout(function () {
            if (self.pageFlip && self.pageFlip.getRender) {
              try {
                self.pageFlip.update();
              } catch (e) {
                /* noop */
              }
            }
          }, 60);
        });
      }
    }

    toggleFullscreen() {
      if (document.fullscreenElement === this) {
        document.exitFullscreen();
      } else if (this.requestFullscreen) {
        this.requestFullscreen();
      }
    }

    buildFlipbook() {
      var self = this;
      this.setStatus('Loading…');

      return window.pdfjsLib
        .getDocument({ url: this.url }).promise
        .then(function (pdf) {
          self.pdf = pdf;
          self.numPages = pdf.numPages;
          // Render the first page first so we know the aspect ratio,
          // then render the rest.
          return self.renderPage(1).then(function (firstImg) {
            return { firstImg: firstImg };
          });
        })
        .then(function (ctx) {
          // A single-page PDF can't flip — show it centered on its own.
          if (self.numPages === 1) {
            self.renderSinglePage(ctx.firstImg);
            return null;
          }

          var ratio = ctx.firstImg.height / ctx.firstImg.width;
          self.initPageFlip(ratio);

          var images = new Array(self.numPages);
          images[0] = ctx.firstImg.src;

          // Render remaining pages sequentially to keep memory in check.
          var chain = Promise.resolve();
          for (var i = 2; i <= self.numPages; i++) {
            (function (pageNum) {
              chain = chain.then(function () {
                return self.renderPage(pageNum).then(function (img) {
                  images[pageNum - 1] = img.src;
                });
              });
            })(i);
          }
          return chain.then(function () {
            return images;
          });
        })
        .then(function (images) {
          if (!images) return; // single-page PDF already rendered
          self.pageFlip.loadFromImages(images);
          self.setStatus('');
          self.updatePageLabel();
          // Let StPageFlip lay out the canvas, then collapse the reserved
          // height so the stage hugs the book (no centering gap above/below).
          setTimeout(function () {
            self.fitStageToContent();
            self.centerView();
          }, 50);
        });
    }

    // Once the book is sized, drop the tall placeholder height so the section
    // doesn't show empty space above and below the spread.
    fitStageToContent() {
      if (this.stage) this.stage.style.minHeight = '0px';
    }

    renderSinglePage(img) {
      img.className = 'pdf-flipbook__single';
      this.viewport.appendChild(img);
      this.fitStageToContent();
      this.setStatus('');
      if (this.pageLabel) this.pageLabel.textContent = '1 / 1';
      // Nothing to flip — hide the page-turn buttons if present.
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
    }

    // Keep a lone page (cover / back cover) centered in the viewport instead
    // of sitting on one half of the two-page spread.
    centerView() {
      if (!this.pageFlip || !this.doublePage) return;
      var canvas = this.viewport.querySelector('canvas');
      if (!canvas) return;

      var orientation = this.pageFlip.getOrientation
        ? this.pageFlip.getOrientation()
        : 'landscape';
      var index = this.pageFlip.getCurrentPageIndex();
      var count = this.pageFlip.getPageCount();
      var shift = 0;

      if (orientation === 'landscape') {
        var quarter = canvas.clientWidth / 4;
        if (index === 0) {
          shift = -quarter; // cover is drawn on the right half
        } else if (index === count - 1) {
          shift = quarter; // back cover is drawn on the left half
        }
      }

      canvas.style.transition = 'transform 0.35s ease';
      canvas.style.transform = shift ? 'translateX(' + shift + 'px)' : 'translateX(0)';
    }

    renderPage(pageNum) {
      var self = this;
      return this.pdf.getPage(pageNum).then(function (page) {
        var base = page.getViewport({ scale: 1 });
        var scale = Math.min(self.maxRenderWidth / base.width, 3);
        var viewport = page.getViewport({ scale: scale });
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        return page
          .render({ canvasContext: context, viewport: viewport }).promise
          .then(function () {
            var img = new Image();
            img.src = canvas.toDataURL('image/jpeg', 0.92);
            img.width = canvas.width;
            img.height = canvas.height;
            // Free the canvas backing store.
            canvas.width = canvas.height = 0;
            return img;
          });
      });
    }

    initPageFlip(ratio) {
      var self = this;
      var PageFlip = window.St && window.St.PageFlip;
      if (!PageFlip) throw new Error('StPageFlip not available');

      var containerWidth = this.viewport.clientWidth || 600;
      // Cap the page height first, then derive width from the PDF ratio so the
      // book never grows taller than the configured height.
      var maxPageHeight = this.maxHeight;
      var maxPageWidth = Math.round(maxPageHeight / ratio);

      var baseWidth = this.doublePage
        ? Math.floor(containerWidth / 2)
        : containerWidth;
      baseWidth = Math.max(300, Math.min(baseWidth, maxPageWidth));
      var baseHeight = Math.round(baseWidth * ratio);

      this.pageFlip = new PageFlip(this.viewport, {
        width: baseWidth,
        height: baseHeight,
        size: 'stretch',
        minWidth: 280,
        maxWidth: maxPageWidth,
        minHeight: Math.round(280 * ratio),
        maxHeight: maxPageHeight,
        maxShadowOpacity: this.shadowOpacity,
        showCover: this.doublePage,
        usePortrait: true,
        mobileScrollSupport: true,
        showPageCorners: true,
        drawShadow: true,
        flippingTime: 900
      });

      this.pageFlip.on('flip', function () {
        self.updatePageLabel();
        self.centerView();
      });
      this.pageFlip.on('changeState', function () {
        self.updatePageLabel();
        // Re-center only once a flip has settled, to avoid mid-animation jumps.
        if (self.pageFlip.getState && self.pageFlip.getState() === 'read') {
          self.centerView();
        }
      });
    }

    updatePageLabel() {
      if (!this.pageLabel || !this.pageFlip) return;
      var current = this.pageFlip.getCurrentPageIndex() + 1;
      var total = this.pageFlip.getPageCount();
      this.pageLabel.textContent = current + ' / ' + total;
    }
  }

  if (!customElements.get('pdf-flipbook')) {
    customElements.define('pdf-flipbook', PdfFlipbook);
  }
})();
